import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Sparkles, Send, Loader2, Lightbulb, TrendingUp, AlertTriangle, Target } from 'lucide-react';
import { toast } from 'sonner';

const insightTypes = [
  { value: 'general', label: 'General Analysis', icon: Sparkles },
  { value: 'occupancy', label: 'Occupancy Insights', icon: TrendingUp },
  { value: 'pricing', label: 'Pricing Optimization', icon: Target },
  { value: 'risk', label: 'Risk Assessment', icon: AlertTriangle },
];

const suggestedQueries = [
  "What are the underperforming zones in this mall?",
  "Suggest optimal category mix for improving footfall",
  "Which brands should we target for vacant units?",
  "Analyze revenue trends and forecast next quarter",
  "Identify high-risk leases expiring soon",
  "What pricing adjustments would maximize occupancy?",
];

export default function AIInsightsPage() {
  const { api } = useAuth();
  const [malls, setMalls] = useState([]);
  const [selectedMall, setSelectedMall] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState([]);
  const [contextType, setContextType] = useState('general');

  useEffect(() => {
    fetchMalls();
  }, []);

  const fetchMalls = async () => {
    try {
      const response = await api.get('/malls');
      setMalls(response.data);
      if (response.data.length > 0) {
        setSelectedMall(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch malls:', error);
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a query');
      return;
    }

    setLoading(true);
    const newInsight = {
      id: Date.now(),
      query,
      response: null,
      loading: true,
      timestamp: new Date(),
    };
    setInsights((prev) => [newInsight, ...prev]);
    setQuery('');

    try {
      const response = await api.post('/ai/insights', {
        query,
        mall_id: selectedMall || null,
        context_type: contextType,
      });

      setInsights((prev) =>
        prev.map((item) =>
          item.id === newInsight.id
            ? { ...item, response: response.data, loading: false }
            : item
        )
      );
    } catch (error) {
      console.error('AI Insight error:', error);
      setInsights((prev) =>
        prev.map((item) =>
          item.id === newInsight.id
            ? {
                ...item,
                response: {
                  insight: 'Failed to generate insight. Please try again.',
                  recommendations: [],
                  confidence: 0,
                },
                loading: false,
              }
            : item
        )
      );
      toast.error('Failed to generate AI insight');
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestedQuery = (suggestion) => {
    setQuery(suggestion);
  };

  const selectedMallName = malls.find((m) => m.id === selectedMall)?.name || 'All Malls';

  return (
    <div className="space-y-6" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-primary" />
            AI Insights
          </h2>
          <p className="text-muted-foreground">Powered by GPT-5.2 for intelligent analysis</p>
        </div>
        <div className="flex gap-3">
          <Select value={selectedMall || 'all'} onValueChange={(v) => setSelectedMall(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-[200px] input-dark" data-testid="select-mall">
              <SelectValue placeholder="Select mall" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Malls</SelectItem>
              {malls.map((mall) => (
                <SelectItem key={mall.id} value={mall.id}>
                  {mall.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Query Panel */}
        <div className="lg:col-span-2 space-y-4">
          {/* Query Input */}
          <Card className="glass-card">
            <CardContent className="p-4">
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex gap-2 mb-4">
                  {insightTypes.map((type) => (
                    <Button
                      key={type.value}
                      type="button"
                      variant={contextType === type.value ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => setContextType(type.value)}
                      className={contextType === type.value ? 'btn-primary' : ''}
                      data-testid={`type-${type.value}`}
                    >
                      <type.icon className="w-3 h-3 mr-1" />
                      {type.label}
                    </Button>
                  ))}
                </div>
                <Textarea
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Ask AI about occupancy, pricing, category mix, risk assessment..."
                  className="input-dark min-h-[100px] resize-none"
                  data-testid="ai-query-input"
                />
                <div className="flex justify-between items-center">
                  <p className="text-xs text-muted-foreground">
                    Context: {selectedMallName}
                  </p>
                  <Button
                    type="submit"
                    className="btn-primary"
                    disabled={loading || !query.trim()}
                    data-testid="submit-query-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Analyzing...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4 mr-2" />
                        Get Insights
                      </>
                    )}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* Insights History */}
          <ScrollArea className="h-[500px]">
            <div className="space-y-4">
              {insights.length === 0 ? (
                <Card className="glass-card">
                  <CardContent className="p-8 text-center">
                    <Sparkles className="w-12 h-12 text-primary/50 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold mb-2">Start with AI Insights</h3>
                    <p className="text-muted-foreground text-sm">
                      Ask questions about your mall portfolio to get AI-powered analysis and recommendations.
                    </p>
                  </CardContent>
                </Card>
              ) : (
                insights.map((item) => (
                  <Card key={item.id} className="glass-card animate-fade-in" data-testid={`insight-${item.id}`}>
                    <CardContent className="p-4">
                      {/* Query */}
                      <div className="flex items-start gap-3 mb-4">
                        <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center flex-shrink-0">
                          <span className="text-sm font-medium text-primary">Q</span>
                        </div>
                        <div>
                          <p className="font-medium">{item.query}</p>
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.timestamp.toLocaleTimeString()}
                          </p>
                        </div>
                      </div>

                      {/* Response */}
                      {item.loading ? (
                        <div className="flex items-center gap-2 text-muted-foreground ml-11">
                          <Loader2 className="w-4 h-4 animate-spin" />
                          <span>Analyzing data...</span>
                        </div>
                      ) : item.response ? (
                        <div className="ml-11 space-y-4">
                          <div className="flex items-start gap-3">
                            <div className="w-8 h-8 rounded-full bg-secondary/20 flex items-center justify-center flex-shrink-0">
                              <Sparkles className="w-4 h-4 text-secondary" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2 mb-2">
                                <Badge variant="outline" className="text-xs">
                                  Confidence: {(item.response.confidence * 100).toFixed(0)}%
                                </Badge>
                              </div>
                              <div className="prose prose-sm prose-invert max-w-none">
                                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                                  {item.response.insight}
                                </p>
                              </div>
                              {item.response.recommendations?.length > 0 && (
                                <div className="mt-4 p-3 rounded-lg bg-muted/30 border border-border/40">
                                  <h4 className="text-sm font-medium flex items-center gap-2 mb-2">
                                    <Lightbulb className="w-4 h-4 text-amber-500" />
                                    Recommendations
                                  </h4>
                                  <ul className="space-y-1">
                                    {item.response.recommendations.map((rec, i) => (
                                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                                        <span className="text-primary">•</span>
                                        {rec}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </CardContent>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </div>

        {/* Suggested Queries */}
        <div className="space-y-4">
          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <Lightbulb className="w-4 h-4 text-amber-500" />
                Suggested Queries
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {suggestedQueries.map((suggestion, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  className="w-full justify-start text-left text-sm h-auto py-3 px-3 hover:bg-primary/10"
                  onClick={() => handleSuggestedQuery(suggestion)}
                  data-testid={`suggestion-${i}`}
                >
                  <span className="line-clamp-2">{suggestion}</span>
                </Button>
              ))}
            </CardContent>
          </Card>

          <Card className="glass-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm">AI Capabilities</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Occupancy Analysis</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Pricing Optimization</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Brand Recommendations</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Risk Assessment</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <div className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-muted-foreground">Category Mix Analysis</span>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
